import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import {
  createObjectUrlFromDocument,
  isSecureFileUrl,
  resolveAssetUrl,
} from "../../utils/secureFileBlobs";

const CATEGORY_OPTIONS = [
  { value: "", label: "Alle categorieën" },
  { value: "voor", label: "Voor" },
  { value: "na", label: "Na" },
  { value: "bewijs", label: "Bewijs" },
  { value: "overig", label: "Overig" },
];

const INITIAL_FILTERS = {
  projectId: "",
  projectNumber: "",
  workorderId: "",
  uploadedBy: "",
  monteurName: "",
  dateFrom: "",
  dateTo: "",
  category: "",
};

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("nl-NL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function toId(value) {
  return String(value?._id || value?.id || value || "").trim();
}

function toProjectLabel(item) {
  const number = item?.projectNummer || item?.number || item?.nummer || "";
  const name = item?.name || item?.projectNaam || item?.naam || item?.title || "";
  if (number && name) return `${number} · ${name}`;
  return number || name || "Project";
}

function toWorkorderLabel(item) {
  const number = item?.werkbonNummer || item?.nummer || "";
  const title = item?.omschrijving || item?.titel || "";
  if (number && title) return `${number} · ${title}`;
  return number || title || "Werkbon";
}

function toMonteurLabel(item) {
  return item?.naam || item?.name || item?.email || "Monteur";
}

function getWorkorderProjectId(item) {
  return String(item?.projectId?._id || item?.projectId || "").trim();
}

function toAssetUrl(rawValue) {
  const raw = String(rawValue || "").trim();
  if (!raw) return "";
  if (raw.startsWith("/api/files/") || /^https?:\/\/[^/]+\/api\/files\//i.test(raw)) {
    return resolveAssetUrl(raw);
  }
  if (raw.startsWith("/uploads/") || raw.startsWith("uploads/")) {
    return resolveAssetUrl(raw);
  }
  if (raw.includes("/")) return resolveAssetUrl(raw);
  return resolveAssetUrl(`/uploads/werkbonnen/${encodeURIComponent(raw)}`);
}

export default function PhotoOverviewPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [draftFilters, setDraftFilters] = useState(INITIAL_FILTERS);

  const [projects, setProjects] = useState([]);
  const [workorders, setWorkorders] = useState([]);
  const [monteurs, setMonteurs] = useState([]);

  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [error, setError] = useState("");

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [previewItem, setPreviewItem] = useState(null);
  const [thumbObjectUrls, setThumbObjectUrls] = useState({});
  const [previewObjectUrl, setPreviewObjectUrl] = useState("");

  const hasMore = page < totalPages;

  const loadFilterOptions = useCallback(async () => {
    try {
      setLoadingFilters(true);
      const [projectRes, workorderRes, monteurRes] = await Promise.all([
        api.get("/projects"),
        api.get("/werkbonnen"),
        api.get("/users", { params: { role: "Monteur" } }),
      ]);

      setProjects(asArray(projectRes?.data));
      setWorkorders(asArray(workorderRes?.data));
      setMonteurs(asArray(monteurRes?.data));
    } catch (err) {
      setError(err?.response?.data?.message || "Filteropties laden mislukt");
    } finally {
      setLoadingFilters(false);
    }
  }, []);

  const loadPhotos = useCallback(async ({ nextPage = 1, append = false, activeFilters = filters } = {}) => {
    try {
      setLoadingPhotos(true);
      setError("");

      const params = {
        page: nextPage,
        limit: 60,
      };

      if (activeFilters.projectId) params.projectId = activeFilters.projectId;
      if (activeFilters.projectNumber) params.projectNumber = activeFilters.projectNumber;
      if (activeFilters.workorderId) params.workorderId = activeFilters.workorderId;
      if (activeFilters.uploadedBy) params.uploadedBy = activeFilters.uploadedBy;
      if (activeFilters.monteurName) params.monteurName = activeFilters.monteurName;
      if (activeFilters.dateFrom) params.dateFrom = activeFilters.dateFrom;
      if (activeFilters.dateTo) params.dateTo = activeFilters.dateTo;
      if (activeFilters.category) params.category = activeFilters.category;

      const res = await api.get("/photos/pl-overview", { params });
      const nextItems = asArray(res?.data?.items);
      const pagination = res?.data?.pagination || {};

      setItems((prev) => (append ? [...prev, ...nextItems] : nextItems));
      setPage(Number(pagination.page || nextPage));
      setTotalPages(Math.max(1, Number(pagination.totalPages || 1)));
      setTotalItems(Number(pagination.total || nextItems.length));
    } catch (err) {
      setError(err?.response?.data?.message || "Foto-overzicht laden mislukt");
      if (!append) {
        setItems([]);
        setPage(1);
        setTotalPages(1);
        setTotalItems(0);
      }
    } finally {
      setLoadingPhotos(false);
    }
  }, [filters]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  useEffect(() => {
    loadPhotos({ nextPage: 1, append: false, activeFilters: filters });
  }, [filters, loadPhotos]);

  useEffect(() => {
    const secureItems = items.filter((item) =>
      isSecureFileUrl(item?.thumbnailUrl || item?.fileUrl || "")
    );
    if (secureItems.length === 0) {
      setThumbObjectUrls({});
      return undefined;
    }

    let cancelled = false;
    const objectUrls = [];

    (async () => {
      const entries = await Promise.all(
        secureItems.map(async (item) => {
          const sourceUrl = item?.thumbnailUrl || item?.fileUrl || "";
          try {
            const objectUrl = await createObjectUrlFromDocument(sourceUrl);
            objectUrls.push(objectUrl);
            return [String(item?._id || ""), objectUrl];
          } catch {
            return [String(item?._id || ""), ""];
          }
        })
      );

      if (cancelled) {
        objectUrls.forEach((url) => window.URL.revokeObjectURL(url));
        return;
      }

      setThumbObjectUrls(
        Object.fromEntries(entries.filter(([key, value]) => key && value))
      );
    })();

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => window.URL.revokeObjectURL(url));
    };
  }, [items]);

  useEffect(() => {
    const sourceUrl = String(previewItem?.fileUrl || "").trim();
    if (!sourceUrl) {
      setPreviewObjectUrl("");
      return undefined;
    }

    if (!isSecureFileUrl(sourceUrl)) {
      setPreviewObjectUrl(toAssetUrl(sourceUrl));
      return undefined;
    }

    let cancelled = false;
    let objectUrl = "";

    (async () => {
      try {
        objectUrl = await createObjectUrlFromDocument(sourceUrl);
        if (!cancelled) {
          setPreviewObjectUrl(objectUrl);
        }
      } catch {
        if (!cancelled) {
          setPreviewObjectUrl("");
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [previewItem]);

  const projectOptions = useMemo(
    () => projects.map((item) => ({ value: toId(item), label: toProjectLabel(item) })),
    [projects]
  );

  const workorderOptions = useMemo(
    () =>
      workorders.map((item) => ({
        value: toId(item),
        label: toWorkorderLabel(item),
        projectId: getWorkorderProjectId(item),
      })),
    [workorders]
  );

  const filteredWorkorderOptions = useMemo(() => {
    const selectedProjectId = String(draftFilters.projectId || "").trim();
    if (!selectedProjectId) return workorderOptions;
    return workorderOptions.filter(
      (option) => String(option.projectId || "") === selectedProjectId
    );
  }, [draftFilters.projectId, workorderOptions]);

  const monteurOptions = useMemo(
    () => monteurs.map((item) => ({ value: toId(item), label: toMonteurLabel(item) })),
    [monteurs]
  );

  function updateDraft(key, value) {
    setDraftFilters((prev) => {
      if (key === "projectId") {
        const nextProjectId = String(value || "").trim();
        const currentWorkorder = workorderOptions.find(
          (option) => option.value === prev.workorderId
        );
        const shouldResetWorkorder =
          prev.workorderId &&
          currentWorkorder &&
          nextProjectId &&
          String(currentWorkorder.projectId || "") !== nextProjectId;

        return {
          ...prev,
          [key]: value,
          workorderId: shouldResetWorkorder ? "" : prev.workorderId,
        };
      }

      return { ...prev, [key]: value };
    });
  }

  function applyFilters(event) {
    event.preventDefault();
    setFilters(draftFilters);
  }

  function resetFilters() {
    setDraftFilters(INITIAL_FILTERS);
    setFilters(INITIAL_FILTERS);
  }

  async function loadMore() {
    if (!hasMore || loadingPhotos) return;
    await loadPhotos({ nextPage: page + 1, append: true, activeFilters: filters });
  }

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Foto-overzicht</h1>
            <p className="text-sm text-slate-600">Alle werkbonfoto&apos;s voor projectleiding, standaard gesorteerd op nieuwste eerst.</p>
          </div>
          <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
            Totaal: {totalItems}
          </div>
        </div>
      </section>

      <form onSubmit={applyFilters} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
          <input
            type="text"
            value={draftFilters.projectNumber}
            onChange={(event) => updateDraft("projectNumber", event.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Projectnummer bevat..."
            aria-label="Projectnummer bevat"
          />

          <select
            value={draftFilters.projectId}
            onChange={(event) => updateDraft("projectId", event.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            disabled={loadingFilters}
          >
            <option value="">Alle projecten</option>
            {projectOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>

          <select
            value={draftFilters.workorderId}
            onChange={(event) => updateDraft("workorderId", event.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            disabled={loadingFilters}
          >
            <option value="">Alle werkbonnen</option>
            {filteredWorkorderOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>

          <select
            value={draftFilters.uploadedBy}
            onChange={(event) => updateDraft("uploadedBy", event.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            disabled={loadingFilters}
          >
            <option value="">Alle monteurs</option>
            {monteurOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>

          <input
            type="text"
            value={draftFilters.monteurName}
            onChange={(event) => updateDraft("monteurName", event.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Monteur naam/e-mail bevat..."
            aria-label="Monteur naam bevat"
          />

          <input
            type="date"
            value={draftFilters.dateFrom}
            onChange={(event) => updateDraft("dateFrom", event.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            aria-label="Datum van"
          />

          <input
            type="date"
            value={draftFilters.dateTo}
            onChange={(event) => updateDraft("dateTo", event.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            aria-label="Datum tot"
          />

          <select
            value={draftFilters.category}
            onChange={(event) => updateDraft("category", event.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          >
            {CATEGORY_OPTIONS.map((option) => (
              <option key={option.value || "all"} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="submit"
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
            disabled={loadingPhotos}
          >
            Filter toepassen
          </button>
          <button
            type="button"
            onClick={resetFilters}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700"
            disabled={loadingPhotos}
          >
            Reset filters
          </button>
          <span className="text-xs text-slate-500">Sortering: nieuwste eerst</span>
        </div>
      </form>

      {error ? (
        <section className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </section>
      ) : null}

      {loadingPhotos && items.length === 0 ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
          Foto&apos;s laden...
        </section>
      ) : null}

      {!loadingPhotos && items.length === 0 ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
          Geen foto&apos;s gevonden voor de gekozen filters.
        </section>
      ) : null}

      {items.length > 0 ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {items.map((item) => (
              (() => {
                const secureSource = item?.thumbnailUrl || item?.fileUrl || "";
                const thumbUrl =
                  thumbObjectUrls[String(item?._id || "")] || toAssetUrl(secureSource);
                return (
              <button
                key={item._id}
                type="button"
                onClick={() => setPreviewItem(item)}
                className="overflow-hidden rounded-xl border border-slate-200 text-left transition hover:border-slate-300 hover:shadow-sm"
              >
                <div className="aspect-square bg-slate-100">
                  {thumbUrl ? (
                    <img
                      src={thumbUrl}
                      alt={item.filename || "Werkbon foto"}
                      className="h-full w-full object-cover"
                      loading="lazy"
                      onError={(event) => {
                        const fullUrl = toAssetUrl(item?.fileUrl || "");
                        if (fullUrl && event.currentTarget.src !== fullUrl) {
                          event.currentTarget.src = fullUrl;
                        }
                      }}
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-slate-500">Geen thumbnail</div>
                  )}
                </div>
                <div className="space-y-1 p-2">
                  <p className="truncate text-xs font-medium text-slate-900">{item.projectName || "-"}</p>
                  <p className="truncate text-[11px] text-slate-600">{item.workorderNumber || item.workorderTitle || "-"}</p>
                  <p className="truncate text-[11px] text-slate-600">{item.uploadedByName || "-"}</p>
                  <div className="flex items-center justify-between gap-2">
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">{item.category || "overig"}</span>
                    <span className="text-[10px] text-slate-500">{formatDateTime(item.createdAt)}</span>
                  </div>
                  {item.workorderId ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(`/werkbon/${item.workorderId}`);
                      }}
                      className="mt-1 rounded border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-50"
                    >
                      Open werkbon
                    </button>
                  ) : null}
                </div>
              </button>
                );
              })()
            ))}
          </div>

          <div className="mt-4 flex items-center justify-center">
            {hasMore ? (
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingPhotos}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 disabled:opacity-60"
              >
                {loadingPhotos ? "Laden..." : "Meer laden"}
              </button>
            ) : (
              <span className="text-xs text-slate-500">Einde van resultaten</span>
            )}
          </div>
        </section>
      ) : null}

      {previewItem ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setPreviewItem(null)}>
          <div
            className="w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">Foto preview</p>
                <p className="text-xs text-slate-500">{previewItem.projectName || "-"}</p>
              </div>
              <div className="flex items-center gap-2">
                {previewItem?.workorderId ? (
                  <button
                    type="button"
                    onClick={() => {
                      navigate(`/werkbon/${previewItem.workorderId}`);
                      setPreviewItem(null);
                    }}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    Open werkbon
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => setPreviewItem(null)}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700"
                >
                  Sluiten
                </button>
              </div>
            </div>

            <div className="grid gap-0 md:grid-cols-[2fr_1fr]">
              <div className="max-h-[70vh] overflow-auto bg-slate-900">
                {previewItem.fileUrl ? (
                  <img
                    src={previewObjectUrl || ""}
                    alt={previewItem.filename || "Foto preview"}
                    className="mx-auto max-h-[70vh] w-full object-contain"
                  />
                ) : (
                  <div className="flex h-[320px] items-center justify-center text-sm text-slate-300">
                    Geen preview beschikbaar
                  </div>
                )}
              </div>
              <div className="space-y-2 p-4 text-sm">
                <InfoRow label="Project" value={previewItem.projectName} />
                <InfoRow label="Werkbon" value={previewItem.workorderNumber || previewItem.workorderTitle} />
                <InfoRow label="Monteur" value={previewItem.uploadedByName} />
                <InfoRow label="Categorie" value={previewItem.category} />
                <InfoRow label="Geüpload op" value={formatDateTime(previewItem.createdAt)} />
                <InfoRow label="Bestand" value={previewItem.filename} />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-sm text-slate-900">{value || "-"}</p>
    </div>
  );
}
